/**
 * Agent Trace Monitor Component
 * 
 * Real-time visualization of all agent API calls.
 * Displays live traces, request/response details, timing, and agent flow.
 */

import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Activity, Download, Trash2, CheckCircle2, XCircle, Clock, Loader2 } from 'lucide-react';
import { apiTracer, APITraceEntry, APITraceSession } from '@/services/apiTracer';
import { cn } from '@/lib/utils';

export function AgentTraceMonitor() {
  const [traces, setTraces] = useState<APITraceEntry[]>([]);
  const [currentSession, setCurrentSession] = useState<APITraceSession | undefined>();
  const [selectedTrace, setSelectedTrace] = useState<APITraceEntry | null>(null);
  const [isEnabled, setIsEnabled] = useState(true);

  useEffect(() => {
    // Subscribe to trace updates
    const unsubscribe = apiTracer.subscribe((trace) => {
      setTraces((prev) => [trace, ...prev].slice(0, 100)); // Keep last 100
    });

    // Subscribe to session updates
    const unsubscribeSession = apiTracer.subscribeToSession((session) => {
      setCurrentSession(session);
    });

    // Load existing traces
    setTraces(apiTracer.getAllTraces());
    setCurrentSession(apiTracer.getCurrentSession());

    return () => {
      unsubscribe();
      unsubscribeSession();
    };
  }, []);

  const handleClear = () => {
    apiTracer.clear();
    setTraces([]);
    setCurrentSession(undefined);
    setSelectedTrace(null);
  };

  const handleToggle = () => {
    const newState = !isEnabled;
    apiTracer.setEnabled(newState);
    setIsEnabled(newState);
  };

  const handleExport = () => {
    if (currentSession) {
      const json = apiTracer.exportSession(currentSession.id);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trace-session-${currentSession.id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const json = apiTracer.exportAllTraces();
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agent-traces-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="h-full flex flex-col gap-4 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-2xl font-bold">Agent API Trace Monitor</h2>
            <p className="text-sm text-muted-foreground">
              Real-time monitoring of all agent API calls
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge variant={isEnabled ? 'default' : 'secondary'}>
            {isEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
          <Button variant="outline" size="sm" onClick={handleToggle}>
            {isEnabled ? 'Disable' : 'Enable'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={handleClear}>
            <Trash2 className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>

      {/* Session Summary */}
      {currentSession && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Current Session</CardTitle>
            <CardDescription className="font-mono text-xs">
              {currentSession.id}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">User Intent</div>
                <div className="font-medium mt-1">{currentSession.userIntent}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total Calls</div>
                <div className="font-medium mt-1">{currentSession.summary.totalCalls}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Success Rate</div>
                <div className="font-medium mt-1">
                  {currentSession.summary.totalCalls > 0
                    ? `${((currentSession.summary.successfulCalls / currentSession.summary.totalCalls) * 100).toFixed(1)}%`
                    : 'N/A'}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total Duration</div>
                <div className="font-medium mt-1">{currentSession.summary.totalDuration}ms</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Agent Chain</div>
                <div className="font-medium mt-1 text-xs">
                  {currentSession.summary.agentChain.join(' → ')}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trace List and Details */}
      <div className="flex-1 grid grid-cols-2 gap-4 min-h-0">
        {/* Trace List */}
        <Card className="flex flex-col">
          <CardHeader>
            <CardTitle>API Calls ({traces.length})</CardTitle>
            <CardDescription>Click on a trace to view details</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0">
            <ScrollArea className="h-full pr-4">
              <div className="space-y-2">
                {traces.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No traces yet. Start using the AI agent to see API calls.
                  </div>
                ) : (
                  traces.map((trace) => (
                    <div
                      key={trace.id}
                      onClick={() => setSelectedTrace(trace)}
                      className={cn(
                        'p-3 rounded-lg border cursor-pointer transition-colors hover:bg-accent',
                        selectedTrace?.id === trace.id && 'bg-accent border-primary'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <TraceStatusIcon status={trace.status} />
                            <span className="font-medium text-sm truncate">
                              {trace.operation}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {trace.agent} → {trace.provider}
                          </div>
                          <div className="text-xs text-muted-foreground truncate mt-1">
                            {trace.request.method} {trace.request.url}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge variant={getProviderVariant(trace.provider)} className="text-xs">
                            {trace.provider}
                          </Badge>
                          {trace.response && (
                            <span className="text-xs text-muted-foreground">
                              {trace.response.duration}ms
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Trace Details */}
        <Card className="flex flex-col">
          <CardHeader>
            <CardTitle>Trace Details</CardTitle>
            <CardDescription>
              {selectedTrace ? `ID: ${selectedTrace.id}` : 'Select a trace to view details'}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0">
            {selectedTrace ? (
              <Tabs defaultValue="overview" className="h-full flex flex-col">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="request">Request</TabsTrigger>
                  <TabsTrigger value="response">Response</TabsTrigger>
                  <TabsTrigger value="context">Context</TabsTrigger>
                </TabsList>
                
                <ScrollArea className="flex-1 mt-4">
                  <TabsContent value="overview" className="space-y-4 mt-0">
                    <div>
                      <div className="text-sm font-medium mb-2">Status</div>
                      <div className="flex items-center gap-2">
                        <TraceStatusIcon status={selectedTrace.status} />
                        <span className="capitalize">{selectedTrace.status}</span>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <div className="text-sm font-medium mb-2">Timing</div>
                      <div className="space-y-1 text-sm">
                        <div>Started: {new Date(selectedTrace.timestamp).toLocaleTimeString()}</div>
                        {selectedTrace.response && (
                          <div>Duration: {selectedTrace.response.duration}ms</div>
                        )}
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <div className="text-sm font-medium mb-2">Agent Flow</div>
                      <div className="space-y-1 text-sm">
                        <div>Agent: {selectedTrace.agent}</div>
                        <div>Operation: {selectedTrace.operation}</div>
                        <div>Provider: {selectedTrace.provider}</div>
                      </div>
                    </div>
                    
                    {selectedTrace.error && (
                      <>
                        <Separator />
                        <div>
                          <div className="text-sm font-medium mb-2 text-destructive">Error</div>
                          <div className="text-sm bg-destructive/10 p-3 rounded-md">
                            {selectedTrace.error.message}
                          </div>
                        </div>
                      </>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="request" className="mt-0">
                    <pre className="text-xs bg-muted p-4 rounded-md overflow-auto">
                      {JSON.stringify(selectedTrace.request, null, 2)}
                    </pre>
                  </TabsContent>
                  
                  <TabsContent value="response" className="mt-0">
                    {selectedTrace.response ? (
                      <pre className="text-xs bg-muted p-4 rounded-md overflow-auto">
                        {JSON.stringify(selectedTrace.response, null, 2)}
                      </pre>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        No response yet
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="context" className="mt-0">
                    <pre className="text-xs bg-muted p-4 rounded-md overflow-auto">
                      {JSON.stringify(selectedTrace.context, null, 2)}
                    </pre>
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                Select a trace from the list to view details
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function TraceStatusIcon({ status }: { status: APITraceEntry['status'] }) {
  switch (status) {
    case 'success':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case 'error':
      return <XCircle className="h-4 w-4 text-destructive" />;
    case 'pending':
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    case 'timeout':
      return <Clock className="h-4 w-4 text-orange-500" />;
    default:
      return null;
  }
}

function getProviderVariant(provider: string): 'default' | 'secondary' | 'outline' {
  switch (provider) {
    case 'google':
      return 'default';
    case 'microsoft':
      return 'default';
    case 'blink':
      return 'secondary';
    case 'internal':
      return 'outline';
    default:
      return 'outline';
  }
}
